/**
 * Slot editing — the shared safety rule (OPEN-053).
 *
 * Every surface that changes an assignment goes through here: the per-site slot pickers, the
 * right-click menu, drag-to-assign, and the bulk wizard. One implementation, so a new entry
 * point cannot quietly ship without the confirm.
 *
 * ── What needs confirming ───────────────────────────────────────────────────────────────
 * The API requires `confirmClear: true` only for an explicit `null`. That is narrower than
 * the rule this UI owes the dispatcher: replacing slot 1's Jane with Bob also *removes Jane*,
 * and on a bulk run across 80 sites that is the destructive case, not the empty-slot fill.
 *
 *   empty slot → someone      no confirm (nothing is lost)
 *   someone    → nobody       CONFIRM (clear; also `confirmClear: true` on the wire)
 *   someone    → someone else CONFIRM (replace; the API does not gate this, we do)
 *   someone    → same person  no-op, no request
 *
 * ── The tri-state on the wire ───────────────────────────────────────────────────────────
 * The body carries ONLY the slot being edited. The untouched slot must be ABSENT, never
 * `null` — `null` means clear. Sending both slots on every save is how an edit to slot 1
 * wipes slot 2.
 */

import type {
  AllAssignments,
  AssignedRequestor,
  AssignmentTargetType,
  BulkAssignBody,
  SetAssignmentsBody,
} from './hooks.js';

export type SlotNumber = 1 | 2;

/** What the dispatcher asked for, before we decide whether to ask them again. */
export type SlotChange =
  | { readonly kind: 'set'; readonly slot: SlotNumber; readonly requestorId: string }
  | { readonly kind: 'clear'; readonly slot: SlotNumber };

export interface ConfirmPrompt {
  readonly title: string;
  readonly body: string;
  readonly confirmLabel: string;
  /** True when the action removes somebody — drives the destructive styling. */
  readonly destructive: boolean;
}

/**
 * The confirmation a change needs, or `null` when it can go straight through.
 *
 * `occupant` is who currently holds the slot (null = empty); `nextName` is the display name of
 * the person being assigned, used only for the prompt text.
 */
export function confirmationFor(
  change: SlotChange,
  occupant: AssignedRequestor | null,
  nextName: string | null,
): ConfirmPrompt | null {
  if (occupant === null) return null; // Filling an empty slot loses nothing.

  if (change.kind === 'clear') {
    return {
      title: `Remove ${occupant.name}?`,
      body: `${occupant.name} will no longer be offered on tickets for this location. Tickets already created keep the requestor they were given.`,
      confirmLabel: 'Remove',
      destructive: true,
    };
  }

  if (change.requestorId === occupant.id) return null; // Re-picking the same person.

  return {
    title: `Replace ${occupant.name}?`,
    body: `Requestor ${change.slot} becomes ${nextName ?? 'the selected requestor'}. ${occupant.name} will no longer be offered on tickets for this location.`,
    confirmLabel: 'Replace',
    destructive: true,
  };
}

/** True when the change would do nothing at all — don't fire a request for it. */
export function isNoop(change: SlotChange, occupant: AssignedRequestor | null): boolean {
  return change.kind === 'clear'
    ? occupant === null
    : occupant !== null && occupant.id === change.requestorId;
}

/**
 * The PUT body for one slot change.
 *
 * Only the edited slot appears. `confirmClear` rides along for a clear because the server
 * demands it; it is meaningless (and omitted) for a set.
 */
export function slotBody(
  targetType: AssignmentTargetType,
  targetId: string,
  change: SlotChange,
): SetAssignmentsBody {
  const base = { targetType, targetId };
  if (change.kind === 'clear') {
    return change.slot === 1
      ? { ...base, slot1: null, confirmClear: true }
      : { ...base, slot2: null, confirmClear: true };
  }
  return change.slot === 1
    ? { ...base, slot1: change.requestorId }
    : { ...base, slot2: change.requestorId };
}

// ─── Bulk ────────────────────────────────────────────────────────────────────

/** A site or well the bulk editor can act on. */
export interface BulkTarget {
  readonly targetType: AssignmentTargetType;
  readonly targetId: string;
  readonly label: string;
}

/**
 * Index the tenant-wide read so a target's occupants are an O(1) lookup.
 *
 * Rejoins the normalised payload: rows carry a `requestorId`, the people ride alongside once.
 * A row whose requestor is missing from that list is DROPPED rather than treated as an empty
 * slot — "nobody is here" and "I could not resolve who is here" must not look the same, since
 * the first silently skips the confirm-before-displacing prompt.
 */
export function indexAssignments(
  payload: AllAssignments | undefined,
): ReadonlyMap<string, { slot1: AssignedRequestor | null; slot2: AssignedRequestor | null }> {
  const out = new Map<
    string,
    { slot1: AssignedRequestor | null; slot2: AssignedRequestor | null }
  >();
  if (payload === undefined) return out;
  const people = new Map(payload.requestors.map((r) => [r.id, r]));
  for (const r of payload.assignments) {
    const who = people.get(r.requestorId);
    if (who === undefined) continue;
    const key = `${r.targetType}:${r.targetId}`;
    const entry = out.get(key) ?? { slot1: null, slot2: null };
    if (Number(r.slot) === 1) entry.slot1 = who;
    else entry.slot2 = who;
    out.set(key, entry);
  }
  return out;
}

export function occupantOf(
  index: ReadonlyMap<string, { slot1: AssignedRequestor | null; slot2: AssignedRequestor | null }>,
  target: BulkTarget,
  slot: SlotNumber,
): AssignedRequestor | null {
  const entry = index.get(`${target.targetType}:${target.targetId}`);
  if (entry === undefined) return null;
  return slot === 1 ? entry.slot1 : entry.slot2;
}

export interface BulkImpact {
  /** Targets that would actually change. */
  readonly changing: readonly BulkTarget[];
  /** Of those, the ones where somebody is displaced — the number the confirm must state. */
  readonly displacing: readonly BulkTarget[];
  /** Distinct names being displaced, for the prompt. */
  readonly displacedNames: readonly string[];
}

/**
 * What a bulk change would actually do, computed BEFORE anything is sent.
 *
 * The dispatcher is about to change up to 500 locations at once with no undo, so the confirm
 * has to say how many existing people it removes — not how many rows were selected. Those two
 * numbers are usually very different: selecting every site for a customer and assigning a
 * requestor to slot 1 typically displaces nobody, and a bulk CLEAR displaces all of them.
 */
export function bulkImpact(
  targets: readonly BulkTarget[],
  index: ReadonlyMap<string, { slot1: AssignedRequestor | null; slot2: AssignedRequestor | null }>,
  change: SlotChange,
): BulkImpact {
  const changing: BulkTarget[] = [];
  const displacing: BulkTarget[] = [];
  const names = new Set<string>();
  for (const t of targets) {
    const occupant = occupantOf(index, t, change.slot);
    if (isNoop(change, occupant)) continue;
    changing.push(t);
    if (occupant !== null) {
      displacing.push(t);
      names.add(occupant.name);
    }
  }
  return { changing, displacing, displacedNames: [...names] };
}

/**
 * The confirmation a bulk change needs, or `null` when nothing is displaced.
 *
 * Unlike the single-target prompt this one always names a COUNT, because "12 sites" is the
 * fact the dispatcher cannot reconstruct from the screen.
 */
export function bulkConfirmation(
  impact: BulkImpact,
  change: SlotChange,
  nextName: string | null,
): ConfirmPrompt | null {
  if (impact.displacing.length === 0) return null;
  const n = impact.displacing.length;
  const where = `${n} ${n === 1 ? 'location' : 'locations'}`;
  const who =
    impact.displacedNames.length <= 3
      ? impact.displacedNames.join(', ')
      : `${impact.displacedNames.slice(0, 3).join(', ')} and ${impact.displacedNames.length - 3} others`;
  return change.kind === 'clear'
    ? {
        title: `Remove requestor ${change.slot} from ${where}?`,
        body: `${who} will no longer be offered on tickets for those locations. Tickets already created keep the requestor they were given. This cannot be undone.`,
        confirmLabel: `Remove from ${where}`,
        destructive: true,
      }
    : {
        title: `Replace requestor ${change.slot} on ${where}?`,
        body: `${who} will be replaced by ${nextName ?? 'the selected requestor'} on ${where}, and will no longer be offered on tickets there. This cannot be undone.`,
        confirmLabel: `Replace on ${where}`,
        destructive: true,
      };
}

/**
 * The POST body for a bulk change.
 *
 * Carries only the targets that would actually change — sending no-ops would inflate the count
 * the server reports back as `updated`, and write an audit row per site claiming a change that
 * did not happen. As with `slotBody`, the untouched slot is ABSENT.
 */
export function bulkBody(targets: readonly BulkTarget[], change: SlotChange): BulkAssignBody {
  const base = {
    targets: targets.map((t) => ({ targetType: t.targetType, targetId: t.targetId })),
  };
  if (change.kind === 'clear') {
    return change.slot === 1
      ? { ...base, slot1: null, confirmClear: true }
      : { ...base, slot2: null, confirmClear: true };
  }
  return change.slot === 1
    ? { ...base, slot1: change.requestorId }
    : { ...base, slot2: change.requestorId };
}

/** The server's cap. Mirrored here so the UI can refuse before the round trip. */
export const BULK_TARGET_CAP = 500;
